import { HStack, Tag, Text, Tooltip } from "@chakra-ui/react";
import type { ColumnDef } from "@tanstack/table-core";
import _ from "lodash";
import { ColumnDefWithTooltip } from "src/components/Table/Table";
import { TagGroupName } from "src/components/Tags/TagGroups/TagGroupName";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { WithId } from "src/types/id";
import { TagGroup } from "src/types/tags";
import { FormTag } from "../types";
import { getDynamicColumnId } from "./columns";
import { ColumnId } from "./constants";

type Row = {
  form: { tags: FormTag[] };
  schoolForm: { tags: FormTag[] } | null;
};

export function buildTagsColumnDef<T extends Row>(
  tagGroup: TagGroup<WithId>
): ColumnDefWithTooltip<T> {
  const headerText = `${tagGroup.name} tags`;
  return {
    id: getDynamicColumnId("tags", tagGroup.id),
    header: () => (
      <TagGroupName name={headerText} isExternal={tagGroup.is_external} />
    ),
    headerText,
    cell: (props) => {
      const tagsWithinGroup =
        props.row.original.schoolForm?.tags.filter(
          (tag) => tag.tagGroupId === tagGroup.id
        ) ?? [];
      return tagsCell(tagsWithinGroup);
    },
  };
}

export function buildFormTagsColumnDef<T extends Row>(): ColumnDef<T> {
  return {
    accessorFn: (row) => row.form.tags,
    id: ColumnId.Tags,
    header: "Form Tags",
    cell: (props) => tagsCell(props.row.original.form.tags),
  };
}

function tagsCell(formTags: FormTag[]) {
  if (!formTags.length) {
    return <></>;
  }

  const mostRecentTag = _.maxBy(formTags, (tag) => tag.createdAt);
  const allTagsString = formTags.map((tag) => tag.name).join(", ");

  const maxWidth = "6rem";

  if (formTags.length > 1 && mostRecentTag) {
    return (
      <Tooltip hasArrow label={allTagsString}>
        <HStack tabIndex={0} width="fit-content">
          <Tag variant="tag">
            <Text overflow="hidden" textOverflow="ellipsis" maxWidth={maxWidth}>
              {mostRecentTag.name}
            </Text>
          </Tag>
          <Tag variant="tag">+{formTags.length - 1}</Tag>
        </HStack>
      </Tooltip>
    );
  }

  if (mostRecentTag) {
    return (
      <Tag variant="tag">
        <TextWithOverflowTooltip
          maxWidth={maxWidth}
          content={mostRecentTag?.name}
          hasArrow
        />
      </Tag>
    );
  }

  return null;
}
